// ─── Keputusan geser di ujung galeri foto produk ─────────────────────────────
//
// Galeri (`ProductImageGallery`) adalah scroller CSS scroll-snap. Slide
// pratinjau produk berikutnya/sebelumnya ditaruh di ujung-ujungnya; begitu
// snap MENDARAT di salah satunya, halaman pindah produk. Modul ini memutuskan
// "sudah mendarat?" secara murni supaya kasus-kasus peliknya teruji:
//
// - JARI MASIH MENEMPEL → tunggu. Menahan jari di atas slide pratinjau
//   bukan keputusan pindah; melepasnya yang memutuskan.
// - Posisi DI TENGAH dua snap → tunggu. Safari & trackpad bisa berhenti
//   sejenak sebelum snap menarik scroller ke titiknya.
// - Harus ada GESTUR EKSPLISIT baru-baru ini (sentuhan, wheel horizontal,
//   tombol panah, ketuk pil). Scroll akibat fokus Tab/VoiceOver juga bisa
//   mendaratkan scroller di pratinjau — itu memperlihatkan, bukan memilih.
// - Toleransi piksel: lebar slide pecahan (mis. 392,7 px) dan overshoot
//   rubber-band iOS (scrollLeft sedikit negatif / melewati maksimum).

/** Jeda tanpa event scroll sebelum posisi dianggap diam. */
export const GALLERY_SETTLE_MS = 150;

/** Umur maksimum gestur eksplisit yang boleh memicu pindah produk. */
export const GALLERY_GESTURE_WINDOW_MS = 2000;

/** Berapa kali pengecekan diulang selagi posisi masih di tengah snap. */
export const GALLERY_SETTLE_MAX_RETRIES = 8;

/** Toleransi piksel: lebar slide pecahan & overshoot rubber-band iOS. */
export const GALLERY_EDGE_TOLERANCE_PX = 2;

/** Indeks slide dari posisi scroll, dibatasi ke `[0, lastIndex]`. */
export function slideIndexFromScroll(
  scrollLeft: number,
  clientWidth: number,
  lastIndex: number
): number {
  if (!(clientWidth > 0) || lastIndex <= 0) return 0;
  const raw = Math.round(scrollLeft / clientWidth);
  return Math.max(0, Math.min(lastIndex, raw));
}

export type GalleryEdgeState = {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
  /** Slide pratinjau di AWAL sudah terpasang (produk sebelumnya). */
  hasLeading: boolean;
  /** Slide pratinjau di UJUNG sudah terpasang (produk berikutnya). */
  hasTrailing: boolean;
  /** Masih ada jari yang menempel di galeri. */
  touching: boolean;
  /** Umur gestur eksplisit terakhir (ms); `Infinity` = belum pernah. */
  msSinceGesture: number;
};

export type GalleryEdgeDecision = "open-leading" | "open-trailing" | "idle" | "wait";

/**
 * Setelah scroller diam `GALLERY_SETTLE_MS`: buka produk di slide ujung,
 * tunggu lagi, atau tak berbuat apa-apa.
 */
export function edgeSettleDecision(state: GalleryEdgeState): GalleryEdgeDecision {
  const { scrollLeft, scrollWidth, clientWidth } = state;
  if (!(clientWidth > 0)) return "idle";
  if (state.touching) return "wait";

  const maxScroll = scrollWidth - clientWidth;
  // Satu slide saja (tak ada yang bisa digeser) — tak ada ujung untuk dituju.
  if (maxScroll <= GALLERY_EDGE_TOLERANCE_PX) return "idle";

  const recent =
    state.msSinceGesture >= 0 && state.msSinceGesture <= GALLERY_GESTURE_WINDOW_MS;
  const atEnd = maxScroll - scrollLeft <= GALLERY_EDGE_TOLERANCE_PX;
  const atStart = scrollLeft <= GALLERY_EDGE_TOLERANCE_PX;
  if (state.hasTrailing && atEnd) return recent ? "open-trailing" : "idle";
  if (state.hasLeading && atStart) return recent ? "open-leading" : "idle";

  const offset = scrollLeft / clientWidth;
  const drift = Math.abs(offset - Math.round(offset)) * clientWidth;
  return drift <= GALLERY_EDGE_TOLERANCE_PX ? "idle" : "wait";
}
